/**
 * Hue Bridge Credential Management Service
 * Handles secure local storage of bridge authentication credentials
 */

import { Platform } from '@/lib/platform'
import { storageLogger } from '@/lib/logger'
import type { SecureStorageInterface, HueBridgeCredentials } from './SecureStorageInterface'
import { LocalSecureStorage } from './LocalSecureStorage'

// Simple Tauri storage implementation that falls back gracefully
class TauriSecureStorageImpl implements SecureStorageInterface {
  private localStorage = new LocalSecureStorage()

  async getItem(key: string): Promise<unknown | null> {
    // For now, just use localStorage
    // TODO: Implement proper Tauri file system storage when Tauri is properly set up
    return this.localStorage.getItem(key)
  }

  async setItem(key: string, value: string): Promise<void> {
    // For now, just use localStorage
    // TODO: Implement proper Tauri file system storage when Tauri is properly set up
    return this.localStorage.setItem(key, value)
  }

  async removeItem(key: string): Promise<void> {
    // For now, just use localStorage
    // TODO: Implement proper Tauri file system storage when Tauri is properly set up
    return this.localStorage.removeItem(key)
  }

  async clear(): Promise<void> {
    return this.localStorage.clear()
  }
}

export class HueCredentialService {
  private storage: SecureStorageInterface

  constructor() {
    this.storage = Platform.isDesktop() 
      ? new TauriSecureStorageImpl()
      : new LocalSecureStorage()
  }

  /**
   * Store bridge credentials securely
   */
  async storeBridgeCredentials(credentials: HueBridgeCredentials): Promise<void> {
    const key = `bridge_${credentials.bridgeId}`
    await this.storage.setItem(key, credentials)
  }

  /**
   * Retrieve bridge credentials
   */
  async getBridgeCredentials(bridgeId: string): Promise<HueBridgeCredentials | null> {
    const key = `bridge_${bridgeId}`
    const result = await this.storage.getItem(key)
    return result as HueBridgeCredentials | null
  }

  /**
   * Remove bridge credentials
   */
  async removeBridgeCredentials(bridgeId: string): Promise<void> {
    const key = `bridge_${bridgeId}`
    await this.storage.removeItem(key)
  }

  /**
   * Get all stored bridge IDs
   */
  async getAllBridgeIds(): Promise<string[]> {
    try {
      if (this.storage instanceof LocalSecureStorage) {
        const keys = await this.storage.getAllKeys()
        return keys
          .filter(key => key.startsWith('bridge_'))
          .map(key => key.replace('bridge_', ''))
      }
      
      // For Tauri, we'd need to list files - simplified for now
      return []
    } catch (error) {
      storageLogger.error('Failed to get bridge IDs:', error)
      return []
    }
  }

  /**
   * Check if credentials exist for a bridge
   */
  async hasBridgeCredentials(bridgeId: string): Promise<boolean> {
    const credentials = await this.getBridgeCredentials(bridgeId)
    return credentials !== null
  }

  /**
   * Update last connected timestamp
   */
  async updateLastConnected(bridgeId: string): Promise<void> {
    const credentials = await this.getBridgeCredentials(bridgeId)
    if (credentials) {
      credentials.lastConnected = Date.now()
      await this.storeBridgeCredentials(credentials)
    }
  }

  /**
   * Clear all stored credentials (use with caution)
   */
  async clearAllCredentials(): Promise<void> {
    await this.storage.clear()
  }
}

// Singleton instance
export const hueCredentialService = new HueCredentialService()